/* This component helps us visualize a diff for a single block of text */

import { Typography } from '@mui/material';
import React from 'react';
import styled from 'styled-components';

/** True if this segment is change-set style (has added/removed). Backend may send { value, count } with no added/removed. */
function isChangeSetSegment(part) {
	return part && (part.added === true || part.removed === true);
}

/** Derive before/after full text from a diff (array of { value, added?, removed? } or legacy { value, count }). */
export function diffToBeforeAfter(diff) {
	if (!diff || !Array.isArray(diff)) return { before: '', after: '' };
	const hasChangeSet = diff.some(isChangeSetSegment);
	if (!hasChangeSet) {
		// Legacy or "after only" format: only value (and maybe count), no added/removed → before is empty
		const after = diff.map((p) => p.value || '').join('');
		return { before: '', after };
	}
	const before = diff.filter((p) => !p.added).map((p) => p.value || '').join('');
	const after = diff.filter((p) => !p.removed).map((p) => p.value || '').join('');
	return { before, after };
}

function isAdded(part) {
	return part.added === true || part.added === 'true';
}
function isRemoved(part) {
	return part.removed === true || part.removed === 'true';
}
/** Unchanged (neutral) = neither added nor removed; belongs in both before and after. */
function isUnchanged(part) {
	return !isAdded(part) && !isRemoved(part);
}

/** True if the given variant has nothing to show (full add = empty before, full delete = empty after). */
export function isDiffEmptyForVariant(diff, variant) {
	const parts = Array.isArray(diff) ? diff : [];
	if (variant === 'before') return parts.filter((p) => isRemoved(p) || isUnchanged(p)).length === 0;
	if (variant === 'after') return parts.filter((p) => isAdded(p) || isUnchanged(p)).length === 0;
	return false;
}

/** Use contextual neutral (light red/green) only when diff has both additions and removals; otherwise neutrals are plain. */
function useContextualNeutrals(parts) {
	const hasRemovals = parts.some((p) => isRemoved(p));
	const hasAdditions = parts.some((p) => isAdded(p));
	return hasRemovals && hasAdditions;
}

const ViewDiff = ({ diff, style, className, variant }) => {
	const parts = diff || [];
	const contextualNeutrals = useContextualNeutrals(parts);

	// Before column = removals + neutral. Removed = red. Neutral = light red only when mixed (adds + removes); else plain.
	if (variant === 'before') {
		const beforeParts = parts.filter((p) => isRemoved(p) || isUnchanged(p));
		if (beforeParts.length === 0) return null;
		return (
			<Typography variant="body1" color="textPrimary" style={style} className={className}>
				{beforeParts.map((part, index) => (
					<Segment
						key={index}
						style={{ padding: '2px 0px 2px'}}
						$color={isRemoved(part) ? 'red' : contextualNeutrals ? 'neutralBefore' : 'plain'}
					>
						{part.value}
					</Segment>
				))}
			</Typography>
		);
	}

	// After column = neutral + additions. Added = green. Neutral = light green only when mixed (adds + removes); else plain.
	if (variant === 'after') {
		const afterParts = parts.filter((p) => isAdded(p) || isUnchanged(p));
		if (afterParts.length === 0) return null;
		return (
			<Typography variant="body1" color="textPrimary" style={style} className={className}>
				{afterParts.map((part, index) => (
					<Segment
						key={index}
						$color={isAdded(part) ? 'green' : contextualNeutrals ? 'neutralAfter' : 'plain'}
						style={{ padding: '2px 0px 2px'}}
					>
						{part.value}
					</Segment>
				))}
			</Typography>
		);
	}

	// Legacy inline (single column): green = added, red = removed, grey = unchanged
	return (
		<Typography variant="body1" color="textPrimary" style={style} className={className}>
			{parts.length === 0 && <Segment $color="grey">No changes</Segment>}
			{parts.length === 1 && (parts[0].value || '').trim() === '' && (
				<Segment $color="grey">No changes</Segment>
			)}
			{parts.map((part, index) => {
				const color = isAdded(part) ? 'green' : isRemoved(part) ? 'red' : 'grey';
				return (
					<Segment key={index} $color={color}>
						{part.value}
					</Segment>
				);
			})}
		</Typography>
	);
};

const Segment = styled.span`
	padding: 2px 2px 2px 4px;
	${(props) => {
		if (props.$color === 'green') {
			return `
				font-weight: 600;
				color: #0d5c0d;
				background-color: #b8e6b8;
			`;
		}
		if (props.$color === 'red') {
			return `
				font-weight: 600;
				color: #8b0000;
				background-color: #ffcccc;
			`;
		}
		if (props.$color === 'neutralBefore') {
			return `
				font-weight: 600;
				color: #8b0000;
				background-color: #ffcccc;
			`;
		}
		if (props.$color === 'neutralAfter') {
			return `
				font-weight: 600;
				color: #0d5c0d;
				background-color: #b8e6b8;
			`;
		}
		if (props.$color === 'grey') {
			return `
				background-color: #f0f0f0;
			`;
		}
		if (props.$color === 'plain') {
			return ``; /* no highlight: inherit text color and no background */
		}
		return '';
	}}
`

export { ViewDiff };